import axios, { CancelTokenSource } from 'axios';
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import Input from 'components/final-form/Input';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import UpsertArticleGroupForm from 'components/medicaments/UpsertArticleGroupForm';
import SearchResultComponent from 'components/SearchResultComponent';
import { FormState } from 'final-form';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Dimmer, Grid, Loader, Message, Modal } from 'semantic-ui-react';
import { deleteArticleGroup, findArticleGroupById, searchArticleGroups } from 'services/articleGroupServices';
import { getAllTestLaboratory } from 'services/testLaboratoryServices';
import { debounce } from 'ts-debounce';
import { ArticleGroupDto, ArticleGroupSearchDto, TestLaboratoryDto, UpsertArticleGroupDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const initialArticleGroupSearch: Partial<ArticleGroupSearchDto> = {};

interface ArticleGroupFieldProps extends FormState {
  values: Partial<ArticleGroupSearchDto>
}

interface Props extends MessageSourceProps {
}

interface State {
  referenceDataLoaded: boolean,
  foundArticleGroups: Array<ArticleGroupDto>;
  articleGroupsLoaded: boolean;
  testLaboratories: Array<TestLaboratoryDto>;
  articleGroup?: UpsertArticleGroupDto;
  articleGroupId?: number;
  addEditModalOpen: boolean;
  loading: boolean;
  searchValues: Partial<ArticleGroupSearchDto>;
  cancelTokenSource: CancelTokenSource;
  errorMessage?: String;
}

class ArticleGroupsView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();


    this.state = {
      referenceDataLoaded: false,
      foundArticleGroups: [],
      testLaboratories: [],
      articleGroupsLoaded: false,
      articleGroup: undefined,
      articleGroupId: undefined,
      addEditModalOpen: false,
      loading: false,
      searchValues: initialArticleGroupSearch,
      cancelTokenSource
    };
    this.loadReferenceData(cancelTokenSource);

  }

  loadReferenceData = async (cancelTokenSource: CancelTokenSource) => {
    try {
      const testLaboratories = await getAllTestLaboratory(cancelTokenSource);

      this.setState({
        testLaboratories: testLaboratories
      });
    } finally {
      this.setState({
        referenceDataLoaded: true
      })
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel("Operation canceled by user");
  }

  openAddEditModal = (id: number | undefined) => {
    const {cancelTokenSource} = this.state;

    if (id) {
      findArticleGroupById(id, cancelTokenSource)
          .then(response => {
            this.showModal(id, response);
          })
          .catch(noop);
    }
  };

  showModal = (id?: number, articleGroup?: UpsertArticleGroupDto) => {
    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        articleGroup: articleGroup,
        articleGroupId: id
      };
    });
  };

  hideModal = () => {
    this.setState(({}: State) => ({
      addEditModalOpen: false,
      articleGroupId: undefined,
      articleGroup: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues} = this.state;

    this.fetchArticleGroups(searchValues);
    this.hideModal();
  }

  deleteArticleGroupById = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteArticleGroup(id, cancelTokenSource)
        .then(response => {
          this.setState(({foundArticleGroups}: State) => ({
            foundArticleGroups: foundArticleGroups.filter(
                (articleGroup: ArticleGroupDto) => articleGroup.id != response
            )
          }));
        })
        .catch((error) => this.handleError(error.response.data));
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: string[] = [];

    if (knownErrors.includes(errorCode)) {
      this.setErrorMessage(getMessage(`articleGroup.error.${errorCode}`))
    } else {
      this.setErrorMessage(getMessage('error.general'))
    }
  }

  setErrorMessage = (error?: String) => {
    this.setState({
      errorMessage: error
    })
  }

  fetchArticleGroups = (values: any) => {

    const {cancelTokenSource} = this.state;
    this.setState({articleGroupsLoaded: false});

    const onFinally = () => {
      this.setState({
        articleGroupsLoaded: true
      })
    };

    searchArticleGroups(values, cancelTokenSource)
        .then(response =>
            this.setState({
              foundArticleGroups: response,
              searchValues: values
            })
        )
        .catch((error) => this.handleError(error.response.data))
        .finally(onFinally);
  }

  handleChange = debounce(({values}: ArticleGroupFieldProps): void => {
    if (!this.mounted) {
      return;
    }
    this.fetchArticleGroups(values);
  }, 300);


  render(): React.ReactNode {
    const {articleGroupsLoaded} = this.state;
    return (
        <PraxStyledSegment>

          <div>
            {this.renderSearchArticleGroupsSegment()}
          </div>

          <StyledViewTableRow>
            {!articleGroupsLoaded
                ? this.renderLoader()
                : this.renderArticleGroupsSegment()}
          </StyledViewTableRow>

          {this.renderUpsertArticleGroupModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchArticleGroupsSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{
              includingInactive: false
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {errorMessage, foundArticleGroups} = this.state;

    const numResults = foundArticleGroups.length;

    return (
        <PxGrid>
          {errorMessage && this.renderErrorSegment()}
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="articleGroupSearchTerm"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={4} verticalAlign={"middle"}>
              <Field
                  name="includingInactive"
                  component={CheckBox}
                  label={getMessage("includingInactive")}
                  toggle={true}
              />
            </Grid.Column>
            <Grid.Column width={9}>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    );
  };

  renderErrorSegment = (): React.ReactNode => {
    const {errorMessage} = this.state;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message negative onDismiss={() => this.setErrorMessage()}>
              <p>{errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">
            {getMessage("articleGroup.loading")}
          </Loader>
        </Dimmer>
    );
  };

  renderArticleGroupsSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundArticleGroups} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundArticleGroups.length}
            rowGetter={this.articleGroupRowGetter}
            rowRenderer={this.foundArticleGroupsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 300,
                flexGrow: 1,
                label: getMessage("articleGroup.description"),
                dataKey: "description"
              },
              {
                width: 300,
                label: getMessage("externalOrderNumber"),
                dataKey: "externalOrderNumber"
              },
              {
                width: 200,
                label: getMessage("articleGroup.externalCode"),
                dataKey: "externalCode",
                cellRenderer: this.externalCodeCellRenderer
              },
              {
                width: 200,
                label: getMessage("articleGroup.testlaboratory"),
                dataKey: "testLaboratoryId",
                cellRenderer: this.testLaboratoryCellRenderer
              },
              {
                width: 200,
                label: getMessage("articleGroup.packageQuantity"),
                dataKey: "packageQuantity"
              },
              {
                width: 400,
                flexGrow: 1,
                label: getMessage("articleGroup.remark"),
                dataKey: "remark"
              },
              {
                width: 100,
                flexGrow: 1,
                label: getMessage("articleGroup.status"),
                dataKey: "active",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 100,
                label: getMessage("actions"),
                dataKey: "id",
                cellRenderer: this.actionsCellRenderer
              }
            ]}
        />
    );
  };

  articleGroupRowGetter = ({index}: any) => {
    const {foundArticleGroups} = this.state;

    Object.assign(foundArticleGroups[index], {index: index + 1});

    return foundArticleGroups[index];
  };

  foundArticleGroupsRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};
    const {foundArticleGroups} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundArticleGroups[index].id)}
        >
          {columns}
        </div>
    );
  };

  externalCodeCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {

      return getMessage(`enum.InterventionExternalCode.${cellData}`)
    }
  }

  testLaboratoryCellRenderer = ({cellData}: any) => {
    const {testLaboratories} = this.state;

    if (cellData && cellData > 0) {
      const foundTL = testLaboratories.filter((tl: TestLaboratoryDto) => tl.id === cellData)
      if (foundTL && foundTL.length > 0) {
        return foundTL[0].name;
      }
    }
    return "";

  }

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return getMessage("articleGroup.active")
    }
    return getMessage("articleGroup.inactive");
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage("msgEdit")}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage("msgDelete")}
              popupMessage={getMessage("articleGroup.confirmDelete")}
              onConfirm={() => this.deleteArticleGroupById(cellData)}
          />
        </React.Fragment>
    );
  };

  renderUpsertArticleGroupModal = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {addEditModalOpen, articleGroup, articleGroupId, testLaboratories} = this.state;

    return (
        <Modal open={addEditModalOpen} closeOnDimmerClick={false} onClose={() => this.hideModal()}>
          <Modal.Header>{getMessage("articleGroup.title")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertArticleGroupForm
                articleGroup={articleGroup}
                articleGroupId={articleGroupId}
                testLaboratories={testLaboratories}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };
}

export default withMessages(ArticleGroupsView);
